import React, { Dispatch, SetStateAction } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { format } from 'date-fns';

import { UseOutletContextPurchase } from '../Purchase';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ProviderOrder } from '../../../../models/provider.order.model';
import { ProviderInvoice } from '../../../../models/provider.invoice.model';
import { legendInvalid, legendValid } from '../../../../tools/legend.data.entry.tool';
import { evaluateLegendValidRequired } from '../../../../scripts/validate.legend.script';
import { expressions } from '../../../../libraries/regular.expressions.library';

import AppLegend from '../../../../components/element/Legend';
import AppInputContact from '../../../../components/element/InputContact';
import AppInputCurrency from '../../../../components/element/InputCurrency';
import AppInputTreasuryAccount from '../../../../components/element/InputTreasuryAccount';

export interface AppPurchaseFormReferenceProps {
  purchase: string,
  record: any | ProviderOrder | ProviderInvoice,
  provider: {value: string, valid: boolean, object: any},
  concerning: {value: string, valid: boolean},
  date: {value: string, valid: boolean},
  currency: {value: string, valid: boolean, object: any},
  treasuryAccount: {value: string, valid: boolean, object: any},
  setProvider: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setConcerning: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDate: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCurrency: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setTreasuryAccount: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>
};

const AppPurchaseFormReference: React.FunctionComponent<AppPurchaseFormReferenceProps> = ({purchase, record, provider, concerning, date, currency, treasuryAccount, setProvider, setConcerning, setDate, setCurrency, setTreasuryAccount}) => {
  const {userLoggedIn, companyForUser, treasuryAccountsForCompany, contactsForCompany, loadContactsForCompany} = UseOutletContextPurchase()
  const {lang} = StateLanguage()

  const handleChangeConcerning = (event: React.ChangeEvent <HTMLFormElement | HTMLTextAreaElement>) => {
    setConcerning({...concerning, value: event.target.value})
  }

  const handleChangeDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setDate({value: event.target.value, valid: true})
      legendValid('legend-date-required')
    } else {
      setDate({value: event.target.value, valid: false})
      legendInvalid('legend-date-required')
    }
  }

  const validateConcerning = () => {
    evaluateLegendValidRequired(expressions.text, concerning, setConcerning, 'legend-concerning-valid', 'legend-concerning-required')
  }

  function currentDate(): string {
    return format((new Date()), 'yyyy-MM-dd')
  }

  return (
    <>
      { userLoggedIn && companyForUser && treasuryAccountsForCompany && contactsForCompany &&
        <div className="card card-flush mb-5">
          <div className="card-header">
            <div className="card-title">
              <h2>
                {lang.labels[`new${purchase !== "expense" ? "Provider" : ""}${purchase.charAt(0).toUpperCase()}${purchase.slice(1)}` as keyof typeof lang.labels]}
                <span className="text-lowercase ms-2">({lang.labels.reference})</span>
              </h2>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="d-flex flex-wrap mb-5">
              <div className="flex-equal me-5">
                <table className="table align-middle gy-1 m-0">
                  <tbody className="fw-bold">
                    <tr>
                      <td className="w-200px text-gray-500">{lang.labels[`provider${purchase.charAt(0).toUpperCase()}${purchase.slice(1)}Reference` as keyof typeof lang.labels]}:</td>
                      <td className="min-w-200px text-gray-800">
                        {purchase === "order" && record.provider_order_number}
                        {purchase === "invoice" && record.provider_invoice_number}
                      </td>
                    </tr>
                    <tr>
                      <td className="w-200px text-gray-500">{lang.labels.company}:</td>
                      <td className="min-w-200px text-gray-800">{record.company.name}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="flex-equal">
                <table className="table align-middle gy-1 m-0">
                  <tbody className="fw-bold">
                    { purchase === "invoice" &&
                      <tr>
                        <td className="w-200px text-gray-500">{lang.labels.providerOrderNumber}:</td>
                        <td className="min-w-200px text-gray-800">{record.provider_order.id.length > 0 ? record.provider_order.provider_order_number : lang.labels.none}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
            <div className="fw-bold fs-6 mb-5">{lang.labels[`pleaseModifyAddOrDeleteFieldsFromProvider${purchase.charAt(0).toUpperCase()}${purchase.slice(1)}` as keyof typeof lang.labels]}</div>
            <div className="row mb-3">
              <label className="form-label required">{lang.labels.provider}</label>
              <div className="fv-row">
                <AppInputContact userLoggedIn={userLoggedIn} companyForUser={companyForUser} contactsForCompany={contactsForCompany} contact={provider} setContact={setProvider} loadContactsForCompany={loadContactsForCompany}></AppInputContact>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label required">{lang.labels.concerning}</label>
              <div className="fv-row">
                <TextareaAutosize className="form-control form-control-solid" name="concerning" minRows={2} value={concerning.value} onChange={handleChangeConcerning} onKeyUp={validateConcerning} onBlur={validateConcerning} />
                <AppLegend attribute={{check: concerning.valid, name: "concerning", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label required">{lang.labels.creationDate}</label>
              <div className="fv-row">
                <input className="form-control form-control-solid" type="date" name="date" max={currentDate()} value={date.value} onChange={handleChangeDate} />
                <AppLegend attribute={{check: date.valid, name: "date", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <label className="form-label required">{lang.labels.currency}</label>
              <div className="fv-row">
                <AppInputCurrency currency={currency} setCurrency={setCurrency}></AppInputCurrency>
              </div>
            </div>
            <div className="row">
              <label className="form-label required">{lang.labels.treasuryAccount}</label>
              <div className="fv-row">
                <AppInputTreasuryAccount solid={true} treasuryAccountsForCompany={treasuryAccountsForCompany} treasuryAccount={treasuryAccount} setTreasuryAccount={setTreasuryAccount}></AppInputTreasuryAccount>
              </div>
            </div>
          </div>
        </div>
      }
    </>
  )
};

export default AppPurchaseFormReference;
